import { UnorderedListOutlined } from '@ant-design/icons'
import { Switch } from 'antd'
import { DspBean, DspListItemBean, ListResponseDspListItemBean, LogBean } from 'common/api/api'
import { dspApi } from 'common/api/entitiesApi.tsx'
import { ActiveStatusSelector } from 'common/components/dictionary/ActiveStatusSelector.tsx'
import { GoToReportButton } from 'common/components/GoToReportButton.tsx'
import { ListActionButton } from 'common/components/ListActionButton.tsx'
import { ListCard } from 'common/components/ListCard'
import { SearchInput } from 'common/components/SearchInput'
import { ListFilters, useAppContext } from 'common/context/AppContext'
import { initListFilters, useListFilters } from 'common/util/useListFilters'
import { apiCall } from 'common/util/Util'
import dayjs from 'dayjs'
import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { COLUMN_ID_WIDTH } from 'common/util/constants.ts'

const DspList = () => {
  const appContext = useAppContext()
  const navigateTo = useNavigate()
  const [filters, setFilters] = useState<ListFilters>(initListFilters())
  const { setFilter } = useListFilters(setFilters)
  const [loading, setLoading] = useState(false)
  const [listData, setListData] = useState<ListResponseDspListItemBean>()

  const loadData = () => {
    apiCall(
      setLoading,
      appContext,
      () =>
        dspApi.dspList(filters.sort?.name, filters.sort?.direction, filters.name, filters.active, {
          query: { size: filters.pageSize, page: filters.page },
        }),
      (data) => setListData(data),
    )
  }

  const toggleActive = (id: number, checked: boolean) => {
    apiCall(
      setLoading,
      appContext,
      () => (checked ? dspApi.massEnableDsp([id]) : dspApi.massDisableDsp([id])),
      () =>
        setListData((prev) =>
          prev
            ? {
                ...prev,
                data: prev.data?.map((item) =>
                  item.data?.id === id ? { ...item, data: { ...item.data, active: checked } } : item,
                ),
              }
            : prev,
        ),
    )
  }

  return (
    <ListCard<DspListItemBean>
      loading={loading}
      title="DSPs"
      createName="DSP"
      createRoute="/dsp/create"
      listRouteKey="/dsp"
      filters={filters}
      setFilters={setFilters}
      listData={listData}
      loadData={loadData}
      columns={[
        {
          title: 'ID',
          dataIndex: 'id',
          width: COLUMN_ID_WIDTH,
        },
        {
          title: 'Name',
          dataIndex: 'name',
          className: 'list-table-name',
          sorter: true,
          ellipsis: true,
          render: (text, record) => (
            <Link style={{ color: '#5888CF', minWidth: 100 }} to={`/dsp/${record.id}/edit`}>
              {text}
            </Link>
          ),
        },
        {
          title: 'Endpoint URL',
          dataIndex: 'endpointUrl',
          sorter: true,
          ellipsis: true,
          width: 300,
        },
        {
          title: 'Last Modified',
          dataIndex: 'modified',
          sorter: true,
          width: 120,
          render: (date) => date && dayjs(date).format('DD MMM YY'),
        },
        {
          title: 'Active',
          dataIndex: 'active',
          sorter: true,
          render: (val, record) => (
            <Switch size="small" checked={val} onChange={(checked) => record.id && toggleActive(record.id, checked)} />
          ),
          width: 70,
        },
        {
          title: 'Actions',
          width: 260,
          render: (_, record: DspBean) => (
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
              <ListActionButton onClick={() => navigateTo(`/logs/${LogBean.EntityEnum.Dsp}/${record.id}`)} tooltip="Logs">
                <UnorderedListOutlined />
              </ListActionButton>
              <GoToReportButton title="Report" filterName="DspId" id={record.id} />
            </div>
          ),
        },
      ]}
    >
      <SearchInput value={filters.name} onChange={(value) => setFilter('name', value)} />
      <ActiveStatusSelector
        placeholder="Filter by Active"
        style={{ minWidth: '100px' }}
        value={filters.active}
        onChange={(value) => setFilter('active', value)}
      />
    </ListCard>
  )
}

export default DspList
